"use client";
import { useAtom } from "jotai";
import { Suspense, useEffect } from "react";
import { progressAtom, serverLoadingAtom } from "../headerHooks";
import { usePathname, useSearchParams } from "next/navigation";
import useIsClient from "../useIsClient";
import ProgressBar from "@/app/components/ui/ProgressBar";

function useRouterConfig() {
  const [, setRouterProgress] = useAtom(progressAtom);
  const [, setServerLoading] = useAtom(serverLoadingAtom);
  
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    // Skip scroll-to-top when a saved position exists and it belongs to the CURRENT page.
    // PageScrollRestore will handle restoring it after content is ready.
    const savedPath = sessionStorage.getItem("scrollRestorePath");
    if (!(sessionStorage.getItem("scrollPosition") && savedPath === pathname)) {
      window.scrollTo(0, 0);
    }
  }, [pathname]);

  useEffect(() => {
    setRouterProgress(false);
    setServerLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, searchParams]);
}

const RouterEventsHand = () => {
  useRouterConfig();
  // Mount the ProgressBar here so it's available globally when this component is mounted
  return <ProgressBar />;
};

/**
 * Using `useSearchParams` in SSG will cause the tree up to the closest `Suspense` to be client-side rendered.
 * You don't want that. Hence we need to wrap usage of `useOnComplete` inside `Suspense`. *Only* this
 * `__RouterEvents` component is client-side rendered, the rest is kept intact.
 *
 * However, if you don't use `useSearchParams` in `useOnComplete` above, you can simply import and use
 * `useOnComplete` in `app/layout.client.tsx` directly.
 */

export function RouterEvents() {
  const isClient = useIsClient();
  return isClient ? (
    <Suspense>
      <RouterEventsHand />
    </Suspense>
  ) : null;
}
